import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// The Loom-style recorder. Three modes:
//   screen_camera → the screen with the webcam in a round bubble (canvas composite)
//   screen        → the screen alone
//   camera        → the webcam alone
// Microphone and, when the browser hands it over, system audio are mixed
// through an AudioContext. MediaRecorder writes WebM; the file, a JPEG
// thumbnail and the measured duration are posted as multipart.
//
// Browsers pause requestAnimationFrame and slow timers down in a hidden tab,
// and a screen recording is exactly the case where the user leaves the tab
// to show something else. A timer in a Web Worker keeps its pace, so it
// drives both the canvas composition and the clock (see tick).
export default class extends Controller {
  static targets = [
    "setup", "stage", "review", "error", "timer", "status",
    "mode", "cameraPreview", "previewHint", "micSelect", "cameraSelect", "startButton",
    "canvas", "livePreview", "countdown", "pauseButton", "pauseLabel",
    "reviewVideo", "title", "saveButton", "progress", "progressBar", "progressLabel"
  ]
  static values = { url: String, maxSeconds: { type: Number, default: 900 }, state: String }

  connect() {
    this.chunks = []
    this.elapsed = 0
    this.previewStream = null
    this.setupDevices()
    this.onKey = (e) => { if (e.key === "Escape" && this.stateValue === "recording") this.stop() }
    document.addEventListener("keydown", this.onKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
    this.stopTicker()
    this.teardownStreams()
    this.stopPreview()
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  }

  // ---------- setup -------------------------------------------------------

  get mode() {
    return (this.modeTargets.find((r) => r.checked) || {}).value || "screen_camera"
  }

  async setupDevices() {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      this.fail(this.t("unsupported"))
      return
    }
    this.modeChanged()
    try {
      await this.startPreview()
      const devices = await navigator.mediaDevices.enumerateDevices()
      this.fillSelect(this.micSelectTarget, devices.filter((d) => d.kind === "audioinput"), this.t("default_mic"))
      this.fillSelect(this.cameraSelectTarget, devices.filter((d) => d.kind === "videoinput"), this.t("default_camera"))
    } catch (e) {
      this.fail(this.t("permission"))
    }
  }

  fillSelect(select, devices, fallbackLabel) {
    select.innerHTML = ""
    devices.forEach((d, i) => {
      const option = document.createElement("option")
      option.value = d.deviceId
      option.textContent = d.label || `${fallbackLabel} ${i + 1}`
      select.appendChild(option)
    })
    select.disabled = devices.length === 0
  }

  modeChanged() {
    this.modeTargets.forEach((r) => r.closest(".recorder-mode")?.classList.toggle("recorder-mode--active", r.checked))
    const wantsCamera = this.mode !== "screen"
    if (this.hasCameraPreviewTarget) this.cameraPreviewTarget.hidden = !wantsCamera
    if (this.hasPreviewHintTarget) this.previewHintTarget.hidden = wantsCamera
    if (this.hasCameraSelectTarget) this.cameraSelectTarget.disabled = !wantsCamera
    if (wantsCamera && !this.previewStream) this.startPreview().catch(() => {})
  }

  async cameraChanged() {
    this.stopPreview()
    await this.startPreview()
  }

  async startPreview() {
    this.previewStream = await navigator.mediaDevices.getUserMedia({
      video: this.cameraConstraints(),
      audio: true
    })
    this.cameraPreviewTarget.srcObject = this.previewStream
  }

  stopPreview() {
    this.previewStream?.getTracks().forEach((t) => t.stop())
    this.previewStream = null
  }

  cameraConstraints() {
    const id = this.hasCameraSelectTarget && this.cameraSelectTarget.value
    return { width: { ideal: 1280 }, height: { ideal: 720 }, ...(id ? { deviceId: { exact: id } } : {}) }
  }

  micConstraints() {
    const id = this.hasMicSelectTarget && this.micSelectTarget.value
    return { echoCancellation: true, noiseSuppression: true, ...(id ? { deviceId: { exact: id } } : {}) }
  }

  // ---------- recording ---------------------------------------------------

  async start() {
    this.clearError()
    this.startButtonTarget.disabled = true
    try {
      const mode = this.mode
      this.stopPreview()

      if (mode !== "camera") {
        this.screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: 30 } }, audio: true
        })
        // The browser's own "Stop sharing" button ends the recording too.
        this.screenStream.getVideoTracks()[0].addEventListener("ended", () => this.stop())
      }
      if (mode !== "screen") {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: this.cameraConstraints(), audio: this.micConstraints()
        })
      } else {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({ audio: this.micConstraints() })
      }

      this.startTicker()
      const stream = await this.buildStream(mode)
      this.setState("stage")
      await this.countdown()

      this.recorder = new MediaRecorder(stream, { mimeType: this.mimeType(), videoBitsPerSecond: 4_000_000 })
      this.chunks = []
      this.recorder.addEventListener("dataavailable", (e) => { if (e.data.size > 0) this.chunks.push(e.data) })
      this.recorder.addEventListener("stop", () => this.finish())
      this.recorder.start(1000)
      this.elapsed = 0
      this.paused = false
      this.startedAt = performance.now()
      this.pausedFor = 0
      this.timerTargets.forEach((t) => { t.hidden = false })
      this.setState("recording")
      this.statusTarget.textContent = this.t("recording")
    } catch (e) {
      this.stopTicker()
      this.teardownStreams()
      this.setState("setup")
      this.startButtonTarget.disabled = false
      this.fail(e?.name === "NotAllowedError" ? this.t("permission") : this.t("failed"))
      this.startPreview().catch(() => {})
    }
  }

  mimeType() {
    return ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
      .find((type) => MediaRecorder.isTypeSupported(type)) || ""
  }

  // Composes the recorded picture on a canvas (screen, with or without the
  // camera bubble) and mixes the audio tracks into one.
  async buildStream(mode) {
    const audio = this.mixAudio()
    if (mode === "camera") {
      this.canvasTarget.hidden = true
      this.livePreviewTarget.hidden = false
      this.livePreviewTarget.srcObject = this.cameraStream
      const video = this.cameraStream.getVideoTracks()[0]
      this.frameSource = this.livePreviewTarget
      return new MediaStream([video, ...audio])
    }

    this.canvasTarget.hidden = false
    this.livePreviewTarget.hidden = true
    this.screenVideo = await this.playHidden(this.screenStream)
    if (mode === "screen_camera") this.cameraVideo = await this.playHidden(this.cameraStream)

    const settings = this.screenStream.getVideoTracks()[0].getSettings()
    const scale = Math.min(1, 1920 / (settings.width || 1920))
    this.canvasTarget.width = Math.round((settings.width || 1920) * scale)
    this.canvasTarget.height = Math.round((settings.height || 1080) * scale)
    this.drawing = true
    this.frameSource = this.canvasTarget

    // Each draw pushes its own frame with requestFrame; a browser without it
    // samples the canvas by itself instead.
    let canvasStream = this.canvasTarget.captureStream(0)
    this.canvasTrack = canvasStream.getVideoTracks()[0]
    if (typeof this.canvasTrack.requestFrame !== "function") {
      canvasStream = this.canvasTarget.captureStream(30)
      this.canvasTrack = null
    }
    this.draw()
    return new MediaStream([...canvasStream.getVideoTracks(), ...audio])
  }

  mixAudio() {
    const sources = [this.cameraStream, this.screenStream].filter((s) => s && s.getAudioTracks().length > 0)
    if (sources.length === 0) return []
    if (sources.length === 1 && !window.AudioContext) return [sources[0].getAudioTracks()[0]]
    this.audioContext = new AudioContext()
    const destination = this.audioContext.createMediaStreamDestination()
    sources.forEach((s) => this.audioContext.createMediaStreamSource(s).connect(destination))
    return destination.stream.getAudioTracks()
  }

  playHidden(stream) {
    const video = document.createElement("video")
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    return new Promise((resolve) => {
      video.addEventListener("loadedmetadata", () => video.play().then(() => resolve(video)))
    })
  }

  draw() {
    if (!this.drawing) return
    const canvas = this.canvasTarget
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = "#000"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    this.drawContain(ctx, this.screenVideo, canvas.width, canvas.height)
    if (this.cameraVideo) this.drawBubble(ctx, canvas)
    this.canvasTrack?.requestFrame()
  }

  drawContain(ctx, video, w, h) {
    const vw = video.videoWidth || w, vh = video.videoHeight || h
    const s = Math.min(w / vw, h / vh)
    const dw = vw * s, dh = vh * s
    ctx.drawImage(video, (w - dw) / 2, (h - dh) / 2, dw, dh)
  }

  // The Loom bubble: a circle, bottom-left, the camera cropped to cover it.
  drawBubble(ctx, canvas) {
    const r = Math.round(canvas.height * 0.13)
    const margin = Math.round(canvas.height * 0.04)
    const cx = margin + r, cy = canvas.height - margin - r
    const video = this.cameraVideo
    const vw = video.videoWidth || 1280, vh = video.videoHeight || 720
    const s = Math.max((2 * r) / vw, (2 * r) / vh)
    const dw = vw * s, dh = vh * s
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()
    ctx.drawImage(video, cx - dw / 2, cy - dh / 2, dw, dh)
    ctx.restore()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.lineWidth = Math.max(3, Math.round(r * 0.06))
    ctx.strokeStyle = "rgba(255,255,255,0.9)"
    ctx.stroke()
  }

  countdown() {
    const el = this.countdownTarget
    el.hidden = false
    return new Promise((resolve) => {
      let n = 3
      el.textContent = n
      const step = () => {
        n -= 1
        if (n === 0) { el.hidden = true; resolve(); return }
        el.textContent = n
        setTimeout(step, 1000)
      }
      setTimeout(step, 1000)
    })
  }

  // About 30 times a second, from the worker: redraws the canvas and keeps
  // the clock on wall time, so neither stalls while the tab is hidden.
  startTicker() {
    const source = "setInterval(() => postMessage(0), 33)"
    try {
      this.workerUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }))
      this.worker = new Worker(this.workerUrl)
      this.worker.onmessage = () => this.tick()
    } catch (_) {
      this.fallbackTicker = setInterval(() => this.tick(), 33)
    }
  }

  stopTicker() {
    this.worker?.terminate()
    this.worker = null
    if (this.workerUrl) URL.revokeObjectURL(this.workerUrl)
    this.workerUrl = null
    clearInterval(this.fallbackTicker)
  }

  tick() {
    if (this.drawing) this.draw()
    if (!this.recorder || this.recorder.state !== "recording") return
    const seconds = Math.floor((performance.now() - this.startedAt - this.pausedFor) / 1000)
    if (seconds !== this.elapsed) {
      this.elapsed = seconds
      this.timerTargets.forEach((t) => { t.textContent = this.clock(this.elapsed) })
    }
    if (this.elapsed >= this.maxSecondsValue) this.stop()
  }

  togglePause() {
    if (!this.recorder) return
    if (this.paused) {
      this.recorder.resume()
      this.paused = false
      this.pausedFor += performance.now() - this.pausedAt
      this.pauseLabelTarget.textContent = this.t("pause")
      this.statusTarget.textContent = this.t("recording")
    } else {
      this.recorder.pause()
      this.paused = true
      this.pausedAt = performance.now()
      this.pauseLabelTarget.textContent = this.t("resume")
      this.statusTarget.textContent = this.t("paused")
    }
  }

  stop() {
    if (!this.recorder || this.recorder.state === "inactive") return
    this.thumbnail = this.captureThumbnail()
    this.recorder.stop()
  }

  captureThumbnail() {
    try {
      const source = this.frameSource
      const out = document.createElement("canvas")
      out.width = 640
      out.height = 360
      const ctx = out.getContext("2d")
      ctx.fillStyle = "#000"
      ctx.fillRect(0, 0, 640, 360)
      const sw = source.videoWidth || source.width, sh = source.videoHeight || source.height
      const s = Math.min(640 / sw, 360 / sh)
      ctx.drawImage(source, (640 - sw * s) / 2, (360 - sh * s) / 2, sw * s, sh * s)
      return new Promise((resolve) => out.toBlob(resolve, "image/jpeg", 0.82))
    } catch (_) {
      return Promise.resolve(null)
    }
  }

  async finish() {
    this.drawing = false
    this.stopTicker()
    this.canvasTrack = null
    this.teardownStreams()
    this.blob = new Blob(this.chunks, { type: this.mimeType() || "video/webm" })
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
    this.blobUrl = URL.createObjectURL(this.blob)
    this.reviewVideoTarget.src = this.blobUrl
    this.setState("review")
    this.statusTarget.textContent = ""
    this.titleTarget.focus()
  }

  teardownStreams() {
    ;[this.screenStream, this.cameraStream].forEach((s) => s?.getTracks().forEach((t) => t.stop()))
    this.screenStream = this.cameraStream = null
    this.audioContext?.close().catch(() => {})
    this.audioContext = null
  }

  // ---------- review & upload -------------------------------------------

  discard() {
    this.chunks = []
    this.blob = null
    this.elapsed = 0
    this.timerTargets.forEach((t) => { t.hidden = true; t.textContent = "0:00" })
    this.startButtonTarget.disabled = false
    this.setState("setup")
    this.startPreview().catch(() => {})
  }

  async save() {
    const title = this.titleTarget.value.trim()
    if (!title) { this.titleTarget.reportValidity(); return }
    if (!this.blob) return
    this.saveButtonTarget.disabled = true
    this.progressTarget.hidden = false

    const form = new FormData()
    form.append("video_message[title]", title)
    form.append("video_message[mode]", this.mode)
    form.append("video_message[duration_seconds]", String(this.elapsed))
    form.append("video_message[video]", this.blob, "videomessaggio.webm")
    const thumb = await this.thumbnail
    if (thumb) form.append("video_message[thumbnail]", thumb, "thumbnail.jpg")

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.urlValue)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.setRequestHeader("X-CSRF-Token", document.querySelector("meta[name='csrf-token']")?.content || "")
    xhr.upload.addEventListener("progress", (e) => {
      if (!e.lengthComputable) return
      const pct = Math.round((e.loaded / e.total) * 100)
      this.progressBarTarget.style.width = `${pct}%`
      this.progressLabelTarget.textContent = this.t("uploading").replace("%{pct}", pct)
    })
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const { url } = JSON.parse(xhr.responseText)
        this.progressLabelTarget.textContent = this.t("uploaded")
        Turbo.visit(url)
      } else {
        this.uploadFailed(xhr)
      }
    })
    xhr.addEventListener("error", () => this.uploadFailed(xhr))
    xhr.send(form)
  }

  uploadFailed(xhr) {
    let message = this.t("upload_failed")
    try { message = JSON.parse(xhr.responseText).errors.join(", ") } catch (_) {}
    this.fail(message)
    this.saveButtonTarget.disabled = false
    this.progressTarget.hidden = true
  }

  // ---------- helpers -----------------------------------------------------

  setState(state) {
    this.stateValue = state
    this.setupTarget.hidden = state !== "setup"
    this.stageTarget.hidden = !(state === "stage" || state === "recording")
    this.reviewTarget.hidden = state !== "review"
  }

  clock(seconds) {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
  }

  fail(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  // Strings come from data-i18n-* on the element so the controller stays
  // locale-free; missing keys fall back to English.
  t(key) {
    const fallback = {
      unsupported: "This browser cannot record video.",
      permission: "Camera, microphone or screen permission was refused.",
      failed: "Recording could not start.",
      recording: "Recording…", paused: "Paused", pause: "Pause", resume: "Resume",
      uploading: "Uploading… %{pct}%", uploaded: "Uploaded, opening…",
      upload_failed: "Upload failed.", default_mic: "Microphone", default_camera: "Camera"
    }
    return this.element.dataset[`i18n${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_(\w)/g, (_, c) => c.toUpperCase())}`] || fallback[key]
  }
}
